import React, { useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { Dialog, DialogTitle, DialogContent, DialogActions } from "@material-ui/core";
import { makeStyles, Grid, Typography, Button, IconButton } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import clientPageStyle from "../styles";
import { Autocomplete, CustomTextField, LoadingFakeText } from "@totalsoft/rocket-webapp-components-deprecated";
import { emptyArray, emptyString } from "constants/defaultValues";
import { useMutationWithErrorHandling, useQueryWithErrorHandling } from "hooks/errorHandling";
import { CURRENCIES_QUERY, GENDER_VALUES_QUERY, CITIES_QUERY } from "features/common/queries";
import { gender } from "constants/queryTypes";
import { get, sequence, set } from "@totalsoft/react-state-lens";
import { onTextBoxChange } from "utils/propertyChangeAdapters";
import { getErrors, isValid, useValidation } from "@totalsoft/pure-validations-react";
import { gdprValidator as contactPersonsValidator } from "../validators";
import { Close } from "@material-ui/icons";
import { SEND_REQUEST_GDPR_CONSENT_EMAIL_LINK, ADD_CONTACT_CONSENT } from "../mutations";
import { addToast } from "components/notifications";
import { partnerConsents } from "constants/partnerConsents";
import { ADD_QUOTE } from "features/assets/mutations";
import useUserData from "hooks/useUserData";
import { eurCurrency } from "constants/currency";
import { EXCHANGE_RATE_QUERY } from "features/assets/queries";
import { GET_CONTACT_PERSONS_FOR_SOLE_TRADER_FEATURE_QUERY, GET_SELECTED_PARTNER_CONTACT_PERSONS } from "../queries";
import { useChangeTrackingLens } from "@totalsoft/change-tracking-react";
import { getTenant } from "providers/TenantProvider";
import { cityTypes, countryCodeConstants } from "features/common/constants";
import { isDirty } from "@totalsoft/change-tracking";

const useStyles = makeStyles(clientPageStyle);

function GdprConsent(props) {
  const { partnerId, partnerName, onCloseGdprConsent, quoteId: existingQuoteId } = props;
  const {
    isExchangeRateFunctionality,
    customerDrivenConsent,
    dealerDrivenConsent,
    allowSoleTraderSearch,
  } = useUserData();
  const { t } = useTranslation();
  const classes = useStyles();
  const { countryCode } = getTenant();
  const existingQuote = !!existingQuoteId;

  const [contactPersonsLens, contactPersonLensDirtyInfo, resetContactPersonsLens] = useChangeTrackingLens([
    { webQuoteContactInfoId: null },
  ]);
  const contactPersons = contactPersonsLens |> get;

  const [validation, validate] = useValidation(contactPersonsValidator);

  const { loading: contactPersonsLoading } = useQueryWithErrorHandling(GET_SELECTED_PARTNER_CONTACT_PERSONS, {
    skip: allowSoleTraderSearch || !existingQuote,
    variables: { quoteId: existingQuoteId },
    onCompleted: (data) => {
      const count = data?.getSelectedPartnerContactPersons?.length;
      if (!count) return;
      resetContactPersonsLens(data.getSelectedPartnerContactPersons);
    },
  });

  const { loading: contactPersonsForSoleTraderFeatureLoading } = useQueryWithErrorHandling(
    GET_CONTACT_PERSONS_FOR_SOLE_TRADER_FEATURE_QUERY,
    {
      skip: !allowSoleTraderSearch,
      variables: { partnerId },
      onCompleted: (data) => {
        const count = data?.getContactPersonsForSoleTraderFeature?.length;
        if (!count) return;
        resetContactPersonsLens(data.getContactPersonsForSoleTraderFeature);
      },
    }
  );

  const { data: genderData } = useQueryWithErrorHandling(GENDER_VALUES_QUERY, { variables: { type: gender } });
  const genderValues = genderData?.genderValues || emptyArray;

  const { data: currenciesData } = useQueryWithErrorHandling(CURRENCIES_QUERY);
  const currencies = currenciesData?.currencies || emptyArray;
  const defaultCurrency = currencies[0];

  const { data: citizenData } = useQueryWithErrorHandling(CITIES_QUERY, {
    skip: countryCode !== countryCodeConstants.italy,
    variables: { cityTypeId: cityTypes.country },
  });
  const citizenValues = citizenData?.cities || emptyArray;

  const { data: exchangeRateData } = useQueryWithErrorHandling(EXCHANGE_RATE_QUERY, {
    skip: !defaultCurrency || !isExchangeRateFunctionality,
    variables: { fromCurrencyCode: defaultCurrency?.code, toCurrencyCode: eurCurrency.code },
  });

  const [addQuote] = useMutationWithErrorHandling(ADD_QUOTE);

  const [requestContactConsent] = useMutationWithErrorHandling(SEND_REQUEST_GDPR_CONSENT_EMAIL_LINK);

  const [addContactConsent] = useMutationWithErrorHandling(ADD_CONTACT_CONSENT);

  const handleAddContactConsent = useCallback(
    (consentCode) => async () => {
      if (!validate(contactPersons)) return;

      const { data } = existingQuote
        ? { data: { addQuote: existingQuoteId } }
        : await addQuote({
            variables: {
              input: {
                partnerId,
                estimationName: t("Asset.GdprEstimationName", { PartnerName: partnerName }),
                currencyId: defaultCurrency.id,
                exchangeRate: exchangeRateData?.exchangeRate?.exchangeRate,
                exchangeRateDate: exchangeRateData?.exchangeRate?.exchangeRateDate,
              },
            },
          });

      const input = { quoteId: data.addQuote, partnerId, consentCode, contactPersons };
      if (consentCode === partnerConsents.dealerConsent) {
        await addContactConsent({ variables: { input } });
        addToast(t("ToastNotifications.Success"), "success");
      }
      if (consentCode === partnerConsents.customerConsent) {
        await requestContactConsent({ variables: { input } });
      }

      onCloseGdprConsent(input.quoteId, existingQuote, consentCode);
    },
    [
      t,
      addQuote,
      validate,
      addContactConsent,
      requestContactConsent,
      partnerId,
      partnerName,
      onCloseGdprConsent,
      exchangeRateData,
      defaultCurrency,
      existingQuoteId,
      contactPersons,
      existingQuote,
    ]
  );

  const handleCloseGdprConsent = useCallback(() => onCloseGdprConsent(existingQuoteId, !!existingQuoteId), [
    existingQuoteId,
    onCloseGdprConsent,
  ]);

  useEffect(() => {
    if (!citizenValues || citizenValues === emptyArray) return;
    if ((contactPersonsLens |> get).some((x) => x.citizenshipId === null || x.citizenshipId === undefined)) {
      (contactPersonsLens |> sequence).map((contactPersonLens) =>
        (contactPersonLens.citizenshipId |> set)(
          citizenValues.find((x) => x.code.toLowerCase() === countryCodeConstants.italy).id
        )
      );
    }
  }, [contactPersonsLens, citizenValues]);

  return (
    <Dialog maxWidth={"md"} fullWidth open={true}>
      <DialogTitle>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item>
            <Typography className={classes.boldTitle}>{t("DialogMessages.GdprTitle")}</Typography>
          </Grid>
          <Grid item>
            <IconButton onClick={handleCloseGdprConsent}>
              <Close />
            </IconButton>
          </Grid>
        </Grid>
      </DialogTitle>
      <DialogContent dividers>
        {(contactPersonsLoading || contactPersonsForSoleTraderFeatureLoading) && <LoadingFakeText lines={3} />}
        {!(contactPersonsLoading || contactPersonsForSoleTraderFeatureLoading) &&
          (contactPersonsLens |> sequence).map((contactPersonLens, idx) => (
            <Grid container key={idx} spacing={2} alignItems="baseline">
              <Grid item xs>
                <Autocomplete
                  fullWidth
                  isClearable
                  label={`${t("DialogMessages.Gender")}*`}
                  valueKey="id"
                  labelKey="name"
                  simpleValue
                  value={(contactPersonLens.genderId |> get) || emptyString}
                  noOptionsText={t("General.AutocompleteNoOptions")}
                  options={genderValues}
                  onChange={contactPersonLens.genderId |> set}
                  error={!isValid(validation[idx].genderId)}
                  helperText={getErrors(validation[idx].genderId)}
                  disabled={(contactPersonLens.webQuoteContactInfoId |> get) !== null}
                />
              </Grid>
              {countryCode === countryCodeConstants.italy && (
                <Grid item xs>
                  <Autocomplete
                    fullWidth
                    label={`${t("DialogMessages.Citizenship")}*`}
                    valueKey="id"
                    labelKey="name"
                    simpleValue
                    value={contactPersonLens.citizenshipId |> get}
                    options={citizenValues}
                    onChange={contactPersonLens.citizenshipId |> set}
                    disabled={(contactPersonLens.webQuoteContactInfoId |> get) !== null}
                  />
                </Grid>
              )}
              <Grid item xs>
                <CustomTextField
                  fullWidth
                  label={`${t("DialogMessages.FirstName")}*`}
                  value={(contactPersonLens.firstName |> get) || emptyString}
                  onChange={contactPersonLens.firstName |> set |> onTextBoxChange}
                  error={!isValid(validation[idx].firstName)}
                  helperText={getErrors(validation[idx].firstName)}
                  disabled={(contactPersonLens.webQuoteContactInfoId |> get) !== null}
                />
              </Grid>
              <Grid item xs>
                <CustomTextField
                  fullWidth
                  label={`${t("DialogMessages.LastName")}*`}
                  value={(contactPersonLens.lastName |> get) || emptyString}
                  onChange={contactPersonLens.lastName |> set |> onTextBoxChange}
                  error={!isValid(validation[idx].lastName)}
                  helperText={getErrors(validation[idx].lastName)}
                  disabled={(contactPersonLens.webQuoteContactInfoId |> get) !== null}
                />
              </Grid>
              <Grid item xs>
                <CustomTextField
                  fullWidth
                  label={`${t("DialogMessages.Email")}*`}
                  value={(contactPersonLens.email |> get) || emptyString}
                  onChange={contactPersonLens.email |> set |> onTextBoxChange}
                  error={!isValid(validation[idx].email)}
                  helperText={getErrors(validation[idx].email)}
                  disabled={
                    (contactPersonLens.webQuoteContactInfoId |> get) !== null &&
                    (contactPersonLens.email |> get) !== null &&
                    !isDirty(contactPersonLensDirtyInfo[idx])
                  }
                />
              </Grid>
            </Grid>
          ))}
      </DialogContent>
      <DialogActions>
        <Grid
          container
          spacing={2}
          alignItems="center"
          justifyContent={customerDrivenConsent && !dealerDrivenConsent ? "flex-end" : "space-between"}
        >
          {dealerDrivenConsent && (
            <Grid item>
              <Typography variant="caption">{t("DialogMessages.GdprInfo")}</Typography>
            </Grid>
          )}
          {dealerDrivenConsent && (
            <Grid item>
              <Button
                className={classes.btn150}
                variant="contained"
                color="primary"
                onClick={handleAddContactConsent(partnerConsents.dealerConsent)}
                //disabled={!isValid(validation)}
              >
                {t("DialogMessages.GdprConfirm")}
              </Button>
            </Grid>
          )}
          {customerDrivenConsent && (
            <Grid item>
              <Button
                className={classes.btn150}
                variant="contained"
                color="primary"
                onClick={handleAddContactConsent(partnerConsents.customerConsent)}
                //disabled={!isValid(validation)}
              >
                {t("DialogMessages.GdprRequest")}
              </Button>
            </Grid>
          )}
        </Grid>
      </DialogActions>
    </Dialog>
  );
}

GdprConsent.propTypes = {
  quoteId: PropTypes.number,
  partnerId: PropTypes.number.isRequired,
  partnerName: PropTypes.string.isRequired,
  onCloseGdprConsent: PropTypes.func.isRequired,
};

export default GdprConsent;
